import React, { useState, useEffect, useRef } from "react"
import {
  Box,
  Typography,
  Button,
  Stack,
  Paper,
  IconButton,
  LinearProgress,
  CircularProgress,
} from "@mui/material"
import ViewInArIcon from "@mui/icons-material/ViewInAr"
import CloudUploadIcon from "@mui/icons-material/CloudUpload"
import DownloadIcon from "@mui/icons-material/Download"
import SwapHorizIcon from "@mui/icons-material/SwapHoriz"
import DeleteIcon from "@mui/icons-material/Delete"
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile"
import { useTranslation } from "react-i18next"
import {
  uploadFile,
  downloadFile,
  deleteFileById,
} from "../../helpers/fileStorage"

const ACCEPTED_EXTENSIONS = ".pytha,.pyt,.pyo,.3ds,.obj,.step,.stp,.igs,.iges,.stl,.dxf,.dwg"

const formatFileSize = (bytes) => {
  if (!bytes) return ""
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const Pytha3DFileUpload = ({ productId, existingFile, onFileUploaded, onFileRemoved }) => {
  const { t } = useTranslation()
  const fileInputRef = useRef(null)
  const replaceInputRef = useRef(null)
  const [file, setFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    setFile(existingFile || null)
  }, [existingFile])

  const handleUpload = async (selectedFile) => {
    if (!selectedFile) return
    setUploading(true)
    setProgress(0)
    try {
      const result = await uploadFile(
        selectedFile,
        "product-pytha3d",
        (pct) => setProgress(pct),
        {
          entity_type: "product-pytha3d",
          entity_id: productId || null,
        }
      )
      setFile(result)
      onFileUploaded?.(result)
    } catch (err) {
      console.error("Pytha3D upload error:", err)
    } finally {
      setUploading(false)
      setProgress(0)
    }
  }

  const handleFileSelect = (e) => {
    const selected = e.target.files?.[0]
    if (selected) handleUpload(selected)
    e.target.value = ""
  }

  const handleReplace = async (e) => {
    const selected = e.target.files?.[0]
    if (!selected) return
    e.target.value = ""
    if (file?.id) {
      await deleteFileById(file.id)
    }
    handleUpload(selected)
  }

  const handleDelete = async () => {
    if (!file?.id) return
    setDeleting(true)
    try {
      await deleteFileById(file.id)
      setFile(null)
      onFileRemoved?.()
    } catch (err) {
      console.error("Pytha3D delete error:", err)
    } finally {
      setDeleting(false)
    }
  }

  const handleDownload = () => {
    if (!file?.id) return
    downloadFile(file.id, file.original_filename || file.name || "pytha3d-file")
  }

  if (uploading) {
    return (
      <Box sx={{ p: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="#64748b">
            {t("Uploading...")} {progress}%
          </Typography>
        </Stack>
        <LinearProgress variant="determinate" value={progress} sx={{ borderRadius: 1 }} />
      </Box>
    )
  }

  if (file) {
    return (
      <Paper
        variant="outlined"
        sx={{ p: 2, borderRadius: 2, border: "1px solid #e2e8f0", bgcolor: "#f8fafc" }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <InsertDriveFileIcon sx={{ fontSize: 32, color: "#8b5cf6" }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              fontWeight={600}
              color="#1e293b"
              sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >
              {file.original_filename || file.name || "pytha3d-file"}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              {file.file_size && (
                <Typography variant="caption" color="#64748b">
                  {formatFileSize(file.file_size)}
                </Typography>
              )}
              {file.created_at && (
                <Typography variant="caption" color="#94a3b8">
                  {new Date(file.created_at).toLocaleDateString()}
                </Typography>
              )}
            </Stack>
          </Box>
          <Stack direction="row" spacing={0.5}>
            <IconButton size="small" onClick={handleDownload} sx={{ color: "#3b82f6" }}>
              <DownloadIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => replaceInputRef.current?.click()}
              sx={{ color: "#d97706" }}
            >
              <SwapHorizIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleDelete}
              disabled={deleting}
              sx={{ color: "#ef4444" }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
        <input
          ref={replaceInputRef}
          type="file"
          accept={ACCEPTED_EXTENSIONS}
          style={{ display: "none" }}
          onChange={handleReplace}
        />
      </Paper>
    )
  }

  return (
    <Box
      onClick={() => fileInputRef.current?.click()}
      sx={{
        p: 3,
        textAlign: "center",
        border: "2px dashed #cbd5e1",
        borderRadius: 2,
        cursor: "pointer",
        bgcolor: "#f8fafc",
        transition: "all 0.2s",
        "&:hover": { borderColor: "#8b5cf6", bgcolor: "#faf5ff" },
      }}
    >
      <ViewInArIcon sx={{ fontSize: 36, color: "#8b5cf6", mb: 0.5 }} />
      <Typography variant="body2" color="#64748b" sx={{ mb: 1 }}>
        {t("Upload Pytha 3D File")}
      </Typography>
      <Button
        variant="outlined"
        size="small"
        startIcon={<CloudUploadIcon />}
        component="span"
        sx={{
          borderColor: "#8b5cf6",
          color: "#8b5cf6",
          textTransform: "none",
          borderRadius: 1.5,
          "&:hover": { borderColor: "#7c3aed", bgcolor: "#faf5ff" },
        }}
      >
        {t("Choose File")}
      </Button>
      <Typography variant="caption" display="block" color="#94a3b8" sx={{ mt: 0.5 }}>
        .pytha, .pyt, .pyo, .3ds, .obj, .step, .stp, .stl, .dxf, .dwg
      </Typography>
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_EXTENSIONS}
        style={{ display: "none" }}
        onChange={handleFileSelect}
      />
    </Box>
  )
}

export default Pytha3DFileUpload
